import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageWrapper from "components/PageWrapper";
import StatsCardSkeleton from "components/skeleton/StatsCardSkeleton";
import { getDashboardDataApi } from "actions/dashboardAction";
import { getActiveDistributor } from "actions/distributorActions";
import {
  ArrowDown,
  ArrowUp,
  ArrowRight,
  BarChart3,
  ClipboardList,
  DollarSign,
  FileArchive,
  Plus,
  Search,
  ShoppingCart,
  Upload,
  Users,
} from "lucide-react";

// SmartBuy design-system tones (from colors_and_type.css).
const TONES = {
  violet: { accent: "from-s360-violet to-s360-violet-300", bubble: "bg-s360-violet-50 text-s360-violet" },
  teal: { accent: "from-s360-teal to-[#5EE7DA]", bubble: "bg-s360-teal-50 text-s360-teal" },
  amber: { accent: "from-s360-warning to-[#FFB000]", bubble: "bg-s360-warning-bg text-s360-warning" },
  green: { accent: "from-s360-success to-[#4CAF50]", bubble: "bg-s360-success-bg text-s360-success" },
};

const StatCard = ({ tone, Icon, label, value, delta, deltaDir = "up", note }) => {
  const t = TONES[tone];
  return (
    <div className="relative overflow-hidden bg-white rounded-md shadow-sm p-[18px] flex flex-col gap-3">
      <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${t.accent}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-[0.06em] text-s360-fg3">
            {label}
          </span>
          <span className="text-[32px] leading-[38px] font-bold tracking-[-0.02em] text-s360-ink tabular-nums">
            {value}
          </span>
        </div>
        <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${t.bubble}`}>
          <Icon className="w-5 h-5" />
        </span>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-s360-fg3">
        {delta && (
          <span
            className={`inline-flex items-center gap-0.5 font-semibold tabular-nums ${
              deltaDir === "up" ? "text-s360-success" : "text-s360-danger"
            }`}
          >
            {deltaDir === "up" ? (
              <ArrowUp className="w-3 h-3" />
            ) : (
              <ArrowDown className="w-3 h-3" />
            )}
            {delta}
          </span>
        )}
        <span>{note}</span>
      </div>
    </div>
  );
};

const PanelHeader = ({ title, description, onViewAll }) => (
  <div className="flex items-start justify-between gap-3 pb-3.5 mb-3.5 border-b border-s360-stroke1">
    <div>
      <h3 className="text-base font-semibold text-s360-fg1">{title}</h3>
      <div className="text-xs text-s360-fg3 mt-0.5">{description}</div>
    </div>
    <button
      type="button"
      onClick={onViewAll}
      className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-sm font-semibold text-s360-violet hover:bg-s360-violet-50 transition-colors"
    >
      View all <ArrowRight className="w-3.5 h-3.5" />
    </button>
  </div>
);

const EmptyState = ({ Icon, title, description, action }) => (
  <div className="flex flex-col gap-2.5 items-center justify-center text-center px-6 py-9 min-h-[220px]">
    <span className="w-14 h-14 rounded-full bg-s360-bg2 text-s360-violet inline-flex items-center justify-center">
      <Icon className="w-5 h-5" />
    </span>
    <h4 className="text-base font-semibold text-s360-fg1">{title}</h4>
    <p className="text-sm text-s360-fg3 max-w-[320px]">{description}</p>
    {action}
  </div>
);

// Prototype `.pill` status styling (bg / text / dot).
const STATUS_PILL = {
  success: { wrap: "bg-s360-success-bg text-s360-success-ink", dot: "bg-s360-success" },
  ready: { wrap: "bg-s360-success-bg text-s360-success-ink", dot: "bg-s360-success" },
  delivered: { wrap: "bg-s360-success-bg text-s360-success-ink", dot: "bg-s360-success" },
  warning: { wrap: "bg-s360-warning-bg text-s360-warning-ink", dot: "bg-s360-warning" },
  pending: { wrap: "bg-s360-warning-bg text-s360-warning-ink", dot: "bg-s360-warning" },
  backorder: { wrap: "bg-s360-warning-bg text-s360-warning-ink", dot: "bg-s360-warning" },
  info: { wrap: "bg-s360-bg2 text-[#002E4D]", dot: "bg-s360-info" },
  shipped: { wrap: "bg-s360-bg2 text-[#002E4D]", dot: "bg-s360-info" },
  violet: { wrap: "bg-s360-violet-50 text-s360-violet-900", dot: "bg-s360-violet" },
  analyzing: { wrap: "bg-s360-violet-50 text-s360-violet-900", dot: "bg-s360-violet" },
};

const statusMeta = (status) => {
  const key = (status || "").toString().toLowerCase().replace(/\s+/g, "_");
  return (
    STATUS_PILL[key] ||
    STATUS_PILL[key.replace(/_/g, "")] ||
    STATUS_PILL.violet
  );
};

const RecentRow = ({ title, sub, metric, metricSub, status, statusLabel }) => {
  const meta = statusMeta(status);
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-1 py-3 border-b border-s360-bg2 last:border-b-0 hover:bg-s360-bg2 rounded">
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="font-mono font-semibold text-[13px] text-s360-fg1 truncate">
          {title}
        </span>
        {sub && <span className="text-xs text-s360-fg3">{sub}</span>}
      </div>
      <div className="font-mono text-xs text-s360-fg3 text-right whitespace-nowrap tabular-nums">
        {metric != null && metric !== "" && (
          <strong className="block text-[13px] font-semibold text-s360-fg1">
            {metric}
          </strong>
        )}
        {metricSub && <span>{metricSub}</span>}
      </div>
      {statusLabel && (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${meta.wrap}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
          {statusLabel}
        </span>
      )}
    </div>
  );
};

// Defensively pick the first non-empty array from a list of candidate keys.
const pickList = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (Array.isArray(v) && v.length > 0) return v;
  }
  return [];
};

const QuickAction = ({ Icon, bubble, title, subtitle, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="bg-white rounded-md shadow-sm p-[18px] flex flex-col gap-3 text-left border border-transparent hover:shadow-md hover:border-s360-violet-100 transition-all"
  >
    <span className={`w-9 h-9 rounded-lg inline-flex items-center justify-center ${bubble}`}>
      <Icon className="w-5 h-5" />
    </span>
    <span className="text-sm font-semibold text-s360-fg1">{title}</span>
    <span className="text-xs text-s360-fg3">{subtitle}</span>
  </button>
);

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [data, setData] = useState({});
  // Distributor gate: block the dashboard until at least one distributor is active.
  const [activeChecked, setActiveChecked] = useState(false);
  const [hasActiveDistributor, setHasActiveDistributor] = useState(false);

  // BOM upload — direct upload here is retired (P10, ec5f74e): the backend
  // now answers any upload with no hub_bom_id with 410 upload_via_hub. Send
  // the user to the Hub picker on /uploaded-bom, the only page that actually
  // owns upload now.
  const openBomUpload = () => navigate("/uploaded-bom");

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await getDashboardDataApi();
      if (response.status) {
        setData(response.data);
      } else {
        setData({});
        setErrorMessage(response.message);
      }
    } catch (error) {
      console.error("Dashboard-data-error---", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      let active = false;
      try {
        const res = await getActiveDistributor();
        active = Boolean(res?.status && res?.data && Object.keys(res.data).length > 0);
      } catch (error) {
        // Network/parse failure: treat as "no active distributor" rather than
        // leaving the page stuck on the loading state forever.
        console.error("Active-distributor-check-error---", error);
        active = false;
      } finally {
        setHasActiveDistributor(active);
        setActiveChecked(true);
      }
      // Only load dashboard data once a distributor is active.
      if (active) fetchDashboardData();
    };
    init();
  }, []);

  // Defensive: support several plausible API shapes for the recent lists.
  const recentBoms = pickList(data, [
    "recent_boms",
    "recentBoms",
    "recent_bom_uploads",
    "recentBomUploads",
  ]);
  const recentOrders = pickList(data, [
    "recent_orders",
    "recentOrders",
  ]);

  // While we confirm whether a distributor is active, show a light loading state.
  if (!activeChecked) {
    return (
      <PageWrapper>
        <div className="flex items-center justify-center py-24 text-sm text-s360-fg3">
          Loading…
        </div>
      </PageWrapper>
    );
  }

  // Gate: no active distributor → prompt setup instead of showing dashboard data.
  if (!hasActiveDistributor) {
    return (
      <PageWrapper>
        <div className="flex flex-col gap-6">
          {/* The suite hero: every product's home opens on a card that names
              the product and its job. QBuy360 was the one home that greeted
              instead — "Welcome back" tells the reader nothing a hero does. */}
          <section
            className="relative overflow-hidden rounded-lg px-[22px] py-5 text-[#EAF3F1]"
            style={{ background: "linear-gradient(115deg,#191334 0%,#221B48 55%,#12403E 100%)" }}
          >
            <div
              className="pointer-events-none absolute -right-10 -top-16 h-80 w-80"
              style={{ background: "radial-gradient(circle,rgba(36,208,192,.2),transparent 65%)" }}
            />
            <div className="relative">
              <div className="font-mono text-[9.5px] uppercase tracking-[.13em] text-s360-teal">
                Qbuy360 · the prototype buying desk
              </div>
              <div className="mt-1.5 text-[22px] font-bold">Price the prototype. Raise one order.</div>
              <p className="mt-1.5 max-w-[64ch] text-[13px] leading-relaxed text-[#A9C0BC]">
                Upload a BOM, price every line live across your connected online distributors, and
                buy the prototype as one purchase order — no re-keying, and no cut of what you
                spend. When it graduates to production volume, RFQ360 takes the same board to
                your vendors.
              </p>
              <div className="mt-3.5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/settings")}
                  className="inline-flex h-9 items-center rounded-md bg-s360-teal px-4 text-sm font-bold text-[#0B2D2A] hover:bg-[#3FDCCD] transition-colors"
                >
                  Connect a distributor
                </button>
              </div>
            </div>
          </section>
          <div className="bg-white rounded-md shadow-sm">
            <EmptyState
              Icon={Plus}
              title="Connect a distributor to get started"
              description="Your dashboard, search, and pricing all run on your distributor connections. Configure at least one distributor to unlock your data."
              action={
                <button
                  type="button"
                  onClick={() => navigate("/settings")}
                  className="mt-1 inline-flex items-center justify-center h-9 px-4 rounded-md bg-s360-violet text-white text-sm font-semibold shadow-sm hover:bg-s360-violet-600 transition-colors"
                >
                  Configure a distributor
                </button>
              }
            />
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="flex flex-col gap-6">
        {/* The suite hero — names the product and its job, and carries the
            real actions. The greeting headline it replaces said nothing the
            sidebar didn't. */}
        <section
          className="relative overflow-hidden rounded-lg px-[22px] py-5 text-[#EAF3F1]"
          style={{ background: "linear-gradient(115deg,#191334 0%,#221B48 55%,#12403E 100%)" }}
        >
          <div
            className="pointer-events-none absolute -right-10 -top-16 h-80 w-80"
            style={{ background: "radial-gradient(circle,rgba(36,208,192,.2),transparent 65%)" }}
          />
          <div className="relative">
            <div className="font-mono text-[9.5px] uppercase tracking-[.13em] text-s360-teal">
              Qbuy360 · the prototype buying desk
            </div>
            <div className="mt-1.5 text-[22px] font-bold">Price the prototype. Raise one order.</div>
            <p className="mt-1.5 max-w-[64ch] text-[13px] leading-relaxed text-[#A9C0BC]">
              Upload a BOM, price every line live across your connected online distributors, and
              buy the prototype as one purchase order — no re-keying, and no cut of what you
              spend. When it graduates to production volume, RFQ360 takes the same board to
              your vendors.
            </p>
            <div className="mt-3.5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openBomUpload}
                className="inline-flex h-9 items-center rounded-md bg-s360-teal px-4 text-sm font-bold text-[#0B2D2A] hover:bg-[#3FDCCD] transition-colors"
              >
                Upload a BOM
              </button>
              <button
                type="button"
                onClick={() => navigate("/search")}
                className="inline-flex h-9 items-center rounded-md border border-white/20 bg-white/[.07] px-4 text-sm font-semibold text-[#EAF3F1] hover:bg-white/[.14] transition-colors"
              >
                Search parts
              </button>
              <button
                type="button"
                onClick={() => navigate("/orders")}
                className="inline-flex h-9 items-center rounded-md border border-white/20 bg-white/[.07] px-4 text-sm font-semibold text-[#EAF3F1] hover:bg-white/[.14] transition-colors"
              >
                My orders
              </button>
            </div>
          </div>
        </section>
        <div className="flex justify-end">
          <button className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-xs font-semibold text-s360-fg2 hover:bg-s360-bg2 transition-colors">
            Last 30 days <ArrowDown className="w-3 h-3" />
          </button>
        </div>

        {/* Stats */}
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {loading ? (
            <>
              <StatsCardSkeleton />
              <StatsCardSkeleton />
              <StatsCardSkeleton />
              <StatsCardSkeleton />
            </>
          ) : (
            <>
              <StatCard
                tone="violet"
                Icon={FileArchive}
                label="Total BOM Uploads"
                value={data?.total_bom_uploads || 0}
                delta="+23%"
                note="from last month"
              />
              <StatCard
                tone="teal"
                Icon={Search}
                label="Total Searches"
                value={data?.total_search_items || 0}
                delta="+18%"
                note="this month"
              />
              <StatCard
                tone="amber"
                Icon={ShoppingCart}
                label="Total Orders"
                value={data?.total_orders || 0}
                delta="-3%"
                deltaDir="down"
                note="vs. prior 30d"
              />
              <StatCard
                tone="green"
                Icon={DollarSign}
                label="Cost Savings"
                value={data?.cost_savings || 0}
                delta="+12%"
                note="vs. list price"
              />
            </>
          )}
        </section>

        {/* Recent BOMs + Recent Orders */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-md shadow-sm p-4">
            <PanelHeader
              title="Recent BOMs"
              description="Latest BOM analyses and their status"
              onViewAll={() => navigate("/uploaded-bom")}
            />
            {recentBoms.length > 0 ? (
              <div className="flex flex-col">
                {recentBoms.map((b, i) => {
                  const title =
                    b?.file_name || b?.name || b?.mpn || b?.title || `BOM #${i + 1}`;
                  const parts =
                    b?.parts ?? b?.parts_count ?? b?.total_parts ?? b?.item_count;
                  const uploaded = b?.uploaded_at_label || b?.created_at_label;
                  const savings =
                    b?.savings ?? b?.est_savings ?? b?.cost_savings;
                  const status = b?.status ?? b?.state;
                  return (
                    <RecentRow
                      key={b?.id ?? title ?? i}
                      title={title}
                      sub={[
                        parts != null ? `${parts} parts` : null,
                        uploaded ? `uploaded ${uploaded}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                      metric={savings != null && savings !== "" ? savings : null}
                      metricSub={savings != null && savings !== "" ? "est. savings" : null}
                      status={status}
                      statusLabel={b?.status_label || b?.statusLabel || status}
                    />
                  );
                })}
              </div>
            ) : (
              <EmptyState
                Icon={Upload}
                title="No BOMs uploaded yet"
                description="Upload a BOM file to start analyzing supplier quotes across Mouser, Digi-Key, Element 14, and 40+ distributors."
                action={
                  <button
                    type="button"
                    onClick={openBomUpload}
                    className="mt-2 inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md bg-s360-violet text-white text-sm font-semibold hover:bg-s360-violet-600 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Upload BOM
                  </button>
                }
              />
            )}
          </div>

          <div className="bg-white rounded-md shadow-sm p-4">
            <PanelHeader
              title="Recent Orders"
              description="Latest orders and their status"
              onViewAll={() => navigate("/orders")}
            />
            {recentOrders.length > 0 ? (
              <div className="flex flex-col">
                {recentOrders.map((o, i) => {
                  const title =
                    o?.po_number || o?.order_number || o?.id || `Order #${i + 1}`;
                  const supplier = o?.supplier || o?.distributor || o?.vendor;
                  const items = o?.items ?? o?.item_count ?? o?.parts;
                  const total =
                    o?.total ?? o?.amount ?? o?.total_amount ?? o?.grand_total;
                  const placed = o?.placed_at_label || o?.created_at_label;
                  const status = o?.status ?? o?.state;
                  return (
                    <RecentRow
                      key={o?.id ?? title ?? i}
                      title={title}
                      sub={[
                        supplier || null,
                        items != null ? `${items} items` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                      metric={total != null && total !== "" ? total : null}
                      metricSub={placed ? `placed ${placed}` : null}
                      status={status}
                      statusLabel={o?.status_label || o?.statusLabel || status}
                    />
                  );
                })}
              </div>
            ) : (
              <EmptyState
                Icon={ShoppingCart}
                title="No orders placed yet"
                description="Start by uploading a BOM to generate purchase orders across your supplier network."
                action={
                  <button 
                  onClick={() => navigate("/search")}
                  className="mt-2 inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md bg-white border border-s360-stroke1 text-s360-fg1 text-sm font-semibold hover:bg-s360-bg2 transition-colors">
                    <ArrowRight className="w-3.5 h-3.5" /> Browse suppliers
                  </button>
                }
              />
            )}
          </div>
        </section>

        {/* Quick actions */}
        <section>
          <h3 className="text-base font-semibold text-s360-fg1 mb-2">Quick actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickAction
              Icon={Upload}
              bubble="bg-s360-violet-50 text-s360-violet"
              title="Upload BOM"
              subtitle="Run analysis across 40+ distributors"
              onClick={() => navigate("/uploaded-bom")}
            />
            <QuickAction
              Icon={Users}
              bubble="bg-s360-teal-50 text-s360-teal"
              title="Manage Suppliers"
              subtitle="Connect APIs, set preferred sources"
              onClick={() => navigate("/suppliers")}
            />
            <QuickAction
              Icon={ClipboardList}
              bubble="bg-s360-warning-bg text-s360-warning"
              title="View Orders"
              subtitle="Track status across all POs"
              onClick={() => navigate("/orders")}
            />
            <QuickAction
              Icon={BarChart3}
              bubble="bg-s360-success-bg text-s360-success"
              title="View Analytics"
              subtitle="Spend, savings, and lead-time trends"
              onClick={() => navigate("/analytics")}
            />
          </div>
        </section>
      </div>
    </PageWrapper>
  );
};

export default DashboardPage;
